/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import * as React from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useConfetti } from '@/hooks/useConfetti';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { BuzzScheduleManagement } from './BuzzScheduleManagement';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Loader } from '@/components/ui/loader';
import { Link } from 'react-router-dom';
import { usePaginatedQuery } from 'convex/react';

// Add URL truncation utility
const truncateUrl = (url: string): string => {
	try {
		const urlObj = new URL(url);
		const domain = urlObj.hostname;
		return `${domain}${urlObj.pathname.length > 15 ? urlObj.pathname.substring(0, 15) + '...' : urlObj.pathname}`;
	} catch {
		return url.substring(0, 30) + '...';
	}
};

type RssFeed = Doc<'rssFeeds'>;
type GoogleTrendsStatus = {
	geo: string;
	feed: RssFeed | null;
	agent?: Doc<'buzzAgents'> | null;
	availableItemsCount: number;
	recentItems: Array<{
		_id: Id<'rssFeedItems'> | Id<'buzzAgentFeedItems'>;
		title: string;
		url: string;
		publishedAt?: string;
		createdAt: string;
		status: string;
		source?: string;
	}>;
};

const formatFreshness = (value?: string) => {
	if (!value) return 'Never refreshed';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return 'Unknown';
	return `${formatDistanceToNow(date)} ago`;
};

export function BuzzAdmin() {
	const { user, isAdmin, isSuperAdmin } = useAuth();
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const [newFeedUrl, setNewFeedUrl] = React.useState('');
	const [newFeedTitle, setNewFeedTitle] = React.useState('');
	const [error, setError] = React.useState<string | null>(null);
	const [trendError, setTrendError] = React.useState<string | null>(null);
	const [isCreating, setIsCreating] = React.useState(false);
	const [isRunningNow, setIsRunningNow] = React.useState(false);
	const [isRefreshingTrends, setIsRefreshingTrends] = React.useState(false);
	const [feedToDelete, setFeedToDelete] = React.useState<{
		id: Id<'rssFeeds'>;
		title: string;
	} | null>(null);
	// Search States
	const [buzzSearchTerm, setBuzzSearchTerm] = React.useState('');
	const [researchSearchTerm, setResearchSearchTerm] = React.useState('');

	// Research Agent Pagination State
	const [researchCursor, setResearchCursor] = React.useState<
		string | undefined
	>(undefined);
	const [isLoadingResearch, setIsLoadingResearch] = React.useState(false);
	const [animationDirection, setAnimationDirection] = React.useState<
		'left' | 'right'
	>('right');

	// Buzz Agents Pagination State
	const [buzzCursor, setBuzzCursor] = React.useState<string | undefined>(
		undefined
	);
	const [isLoadingBuzz, setIsLoadingBuzz] = React.useState(false);
	const [buzzAnimationDirection, setBuzzAnimationDirection] = React.useState<
		'left' | 'right'
	>('right');

	// Research Agent Query with Pagination
	const researchResult = useQuery(
		api.researchAgent.list,
		user?.id && isAdmin
			? {
					cursor: researchCursor,
					numItems: 3,
					userId: user.id as unknown as Id<'users'>,
				}
			: 'skip'
	);
	const researchItems = React.useMemo(() => researchResult?.page || [], [researchResult?.page]);
	// Only consider there are more items if we got a full page AND there's a cursor
	const hasMoreResearch =
		!!researchResult?.continueCursor && researchItems.length >= 3;
	
	// Filter research items based on search term
	const filteredResearchItems = React.useMemo(() => {
		if (!researchSearchTerm) return researchItems;
		
		return researchItems.filter((item: any) => {
			const searchLower = researchSearchTerm.toLowerCase();
			return (
				item.subject?.toLowerCase().includes(searchLower) ||
				item.rawResponse?.toLowerCase().includes(searchLower)
			);
		});
	}, [researchItems, researchSearchTerm]);

	// Buzz Agents Query with Pagination
	const BUZZ_ITEMS_PER_PAGE = 3;
	const buzzResult = useQuery(
		api.buzzAgentFeedItems.listForUser,
		user?._id
			? {
					userId: user._id as unknown as Id<'users'>,
					includePublic: true,
					paginationOpts: {
						numItems: BUZZ_ITEMS_PER_PAGE,
						cursor: buzzCursor ?? null,
					},
				}
			: 'skip'
	);
	const buzzItems = React.useMemo(() => buzzResult?.page || [], [buzzResult?.page]);
	// Only consider there are more items if we got a full page AND there's a cursor
	const hasMoreBuzz = !!buzzResult?.continueCursor && buzzItems.length >= BUZZ_ITEMS_PER_PAGE;
	
	// Filter buzz items based on search term
	const filteredBuzzItems = React.useMemo(() => {
		if (!buzzSearchTerm) return buzzItems;
		
		return buzzItems.filter((item: any) => {
			const searchLower = buzzSearchTerm.toLowerCase();
			return (
				item.headline?.toLowerCase().includes(searchLower) ||
				item.summary?.toLowerCase().includes(searchLower) ||
				item.keyword?.toLowerCase().includes(searchLower) ||
				item.agentName?.toLowerCase().includes(searchLower) ||
				item.source?.toLowerCase().includes(searchLower) ||
				item.rationale?.toLowerCase().includes(searchLower)
			);
		});
	}, [buzzItems, buzzSearchTerm]);

	// Handle Research Navigation
	const handleNextResearch = React.useCallback(() => {
		if (!researchResult?.continueCursor || isLoadingResearch) return;
		setAnimationDirection('right');
		setIsLoadingResearch(true);
		setResearchCursor(researchResult.continueCursor);
		setTimeout(() => setIsLoadingResearch(false), 300);
	}, [researchResult?.continueCursor, isLoadingResearch]);

	const handlePreviousResearch = React.useCallback(() => {
		if (isLoadingResearch) return;
		setAnimationDirection('left');
		setIsLoadingResearch(true);
		setResearchCursor(undefined);
		setTimeout(() => setIsLoadingResearch(false), 300);
	}, [isLoadingResearch]);

	// Handle Buzz Agents Navigation
	const handleNextBuzz = React.useCallback(() => {
		if (!buzzResult?.continueCursor || isLoadingBuzz) return;
		setBuzzAnimationDirection('right');
		setIsLoadingBuzz(true);
		setBuzzCursor(buzzResult.continueCursor);
		setTimeout(() => setIsLoadingBuzz(false), 300);
	}, [buzzResult?.continueCursor, isLoadingBuzz]);

	const handlePreviousBuzz = React.useCallback(() => {
		if (isLoadingBuzz) return;
		setBuzzAnimationDirection('left');
		setIsLoadingBuzz(true);
		setBuzzCursor(undefined);
		setTimeout(() => setIsLoadingBuzz(false), 300);
	}, [isLoadingBuzz]);

	// Mutations
	const createFeed = useMutation(api.rssFeeds.insert);
	const toggleFeed = useMutation(api.rssFeeds.toggleFeed);
	const deleteFeed = useMutation(api.rssFeeds.deleteFeed);
	const runScheduleNow = useAction(api.scheduledTasks.runScheduleNow);
	const refreshGoogleTrends = useAction(
		(api as any).buzzTrends.refreshGoogleTrends
	);

	// Queries with error handling
	const feedsQuery = useQuery(api.rssFeeds.list);
	const feedsList: RssFeed[] = Array.isArray(feedsQuery) ? feedsQuery : [];

	// Get all feed item counts at once
	const feedItemCounts = useQuery(api.rssFeedItems.getAllNewItemsCounts);
	const googleTrendsStatus = useQuery(
		(api as any).buzzTrends.getGoogleTrendsStatus,
		user?._id && isAdmin
			? {
					userId: user._id as unknown as Id<'users'>,
					requestingUserId: user._id as unknown as Id<'users'>,
					geo: 'US',
				}
			: 'skip'
	) as GoogleTrendsStatus | undefined;
	const googleTrendsFeed = googleTrendsStatus?.feed ?? null;
	const isTrendStatusLoading =
		user?._id && isAdmin && googleTrendsStatus === undefined;

	const schedules = useQuery(api.schedules.getAllSchedules);

	const rssFeedSchedule = React.useMemo(() => {
		return schedules?.find((s: { type: string }) => s.type === 'rss_feed');
	}, [schedules]);

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='border-orange-500'>
					<CardHeader>
						<CardTitle className='text-2xl font-bold mb-2 text-orange-500'>
							{!user
								? 'Authentication Required'
								: 'Admin Access Required'}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Alert>
							<AlertTitle>
								{!user ? 'Please Sign In' : 'Access Denied'}
							</AlertTitle>
							<AlertDescription>
								{!user
									? 'You need to be signed in to access this page.'
									: 'You need admin privileges to access this page.'}
							</AlertDescription>
						</Alert>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleCreateFeed = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			setIsCreating(true);
			setError(null);
			const errors: string[] = [];

			// Basic validation
			if (!newFeedUrl) {
				errors.push('🔥 Feed URL is required');
			} else {
				// URL validation
				try {
					const url = new URL(newFeedUrl);
					if (!url.protocol.startsWith('http')) {
						errors.push(
							'🔥 Feed URL must start with http:// or https://'
						);
					}
				} catch {
					errors.push('🔥 Invalid feed URL format');
				}
			}
			if (!newFeedTitle) {
				errors.push('🔥 Feed Title is required');
			}

			// Check for duplicate URL
			const existingFeed = feedsList.find(
				(feed) => feed.url.toLowerCase() === newFeedUrl.toLowerCase()
			);
			if (existingFeed) {
				errors.push('🔥 This feed URL already exists');
			}

			if (errors.length > 0) {
				setError(errors.join('\n'));
				errors.forEach((error) => {
					toast({
						title: '🔥 Validation Error 🔥',
						description: error,
						duration: 3000,
						variant: 'destructive',
						style: { backgroundColor: '#FF8C00', color: 'white' },
					});
				});
				return;
			}

			await createFeed({
				url: newFeedUrl.trim(),
				title: newFeedTitle.trim(),
				userId: user.id as any,
			});

			setNewFeedUrl('');
			setNewFeedTitle('');
			setError(null);

			triggerConfetti();
			toast({
				title: '✨ Feed Added ✨',
				description: 'Fallback feed successfully added',
				duration: 3000,
			});
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : 'Failed to add feed';
			setError(errorMsg);
			toast({
				title: '🔥 Error 🔥',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
				style: { backgroundColor: '#FF8C00', color: 'white' },
			});
		} finally {
			setIsCreating(false);
		}
	};

	const handleToggleFeed = async (feedId: Id<'rssFeeds'>) => {
		try {
			await toggleFeed({ feedId });
			toast({
				title: '✨ Feed Updated',
				description: 'Feed status updated successfully',
				duration: 3000,
			});
		} catch (error) {
			const errorMsg =
				error instanceof Error
					? error.message
					: 'Failed to update feed';
			setError(errorMsg);
			toast({
				title: '🔥 Error',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleDeleteFeed = async (feedId: Id<'rssFeeds'>) => {
		try {
			await deleteFeed({ feedId });
			setFeedToDelete(null);
			toast({
				title: '✨ Feed Deleted',
				description: 'Feed deleted successfully',
				duration: 3000,
			});
		} catch (error) {
			const errorMsg =
				error instanceof Error
					? error.message
					: 'Failed to delete feed';
			setError(errorMsg);
			toast({
				title: '🔥 Error',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleRunNow = async () => {
		if (!rssFeedSchedule) {
			const errorMsg = 'No fallback feed schedule found';
			setError(errorMsg);
			toast({
				title: '❌ Error',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		try {
			setIsRunningNow(true);
			await runScheduleNow({ scheduleId: rssFeedSchedule._id });
			toast({
				title: '🚀 Feeds Updating',
				description: 'Fallback feeds are being fetched...',
				duration: 3000,
			});
		} catch (error) {
			const errorMsg =
				error instanceof Error
					? error.message
					: 'Failed to run feed update';
			setError(errorMsg);
			toast({
				title: '❌ Error',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsRunningNow(false);
		}
	};

	const handleRefreshGoogleTrends = async () => {
		if (!user?._id) {
			const errorMsg = 'You must be signed in to refresh Buzz Agents';
			setTrendError(errorMsg);
			toast({
				title: 'Error',
				description: errorMsg,
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		try {
			setIsRefreshingTrends(true);
			setTrendError(null);
			const result = await refreshGoogleTrends({
				userId: user._id as unknown as Id<'users'>,
				requestingUserId: user._id as unknown as Id<'users'>,
				geo: 'US',
				limit: 25,
			});

			if (!result?.ok) {
				const errorMsg =
					result?.summary ||
					result?.error ||
					'Buzz Agents refresh failed';
				setTrendError(errorMsg);
				toast({
					title: 'Buzz Agent Refresh Failed',
					description: errorMsg,
					variant: 'destructive',
					duration: 5000,
				});
				return;
			}

			toast({
				title: 'Buzz Agents Refreshed',
				description:
					result.summary ||
					`Loaded ${result.fetched ?? 0} current topics.`,
				duration: 4000,
			});
		} catch (error) {
			const errorMsg =
				error instanceof Error
					? error.message
					: 'Buzz Agents refresh failed';
			setTrendError(errorMsg);
			toast({
				title: 'Buzz Agent Refresh Failed',
				description: errorMsg,
				variant: 'destructive',
				duration: 5000,
			});
		} finally {
			setIsRefreshingTrends(false);
		}
	};

	return (
		<div className='relative container mx-auto p-6 pb-80'>
			<>
				<div className='flex justify-between items-center mb-6'>
					<div>
						<h1 className='text-3xl font-bold mb-2 pt-8'>
							Buzz Agents
						</h1>
						<p className='text-xl text-muted-foreground mb-2'>
							Topic intelligence for Content Machine curation
						</p>
						<p className='text-lg text-gray-500'>
							Ready Topics:{' '}
							{googleTrendsStatus?.availableItemsCount ?? 0}
						</p>
					</div>
				</div>
			</>

			<div className='grid gap-8'>
				{/* Buzz Agents Section */}
				<Card>
					<CardHeader>
						<CardTitle className='text-4xl font-bold mb-2 text-purple-500 flex items-center gap-2'>
							<span className='text-pink-500'>Buzz Agents</span>
							<Badge
								variant='secondary'
								className='text-sm border border-white/70 bg-gradient-to-r from-purple-900  to-purple-500 text-white/90'
							>
								Watcher Builder
							</Badge>
						</CardTitle>
						<p className='text-muted-foreground'>
							Curated topics and research signals for Content Machine
						</p>
					</CardHeader>
					<CardContent>
						<div className='space-y-6'>
							<div className='rounded-lg border border-purple-500/30 bg-purple-950/20 p-4'>
								<div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
									<div>
										<p className='text-xs uppercase tracking-wide text-muted-foreground'>
											Trend Intake
										</p>
										<h3 className='text-xl font-semibold text-pink-400'>
											Live Buzz Agent topics
										</h3>
										<p className='mt-1 text-sm text-muted-foreground'>
											Refresh the Prometheus Buzz Agent with current topics for Content Machine generation.
										</p>
									</div>
									<Button
										onClick={handleRefreshGoogleTrends}
										disabled={isRefreshingTrends}
										className='bg-gradient-to-r from-purple-600 to-pink-600 text-white'
									>
										{isRefreshingTrends
											? 'Refreshing...'
											: 'Refresh Buzz Agents'}
									</Button>
								</div>

								<div className='mt-4 grid gap-4 md:grid-cols-3'>
									<div>
										<p className='text-xs uppercase text-muted-foreground'>
											Agent
										</p>
										<p className='font-medium'>
											{googleTrendsStatus?.agent?.name ?? 'Prometheus'}
										</p>
									</div>
									<div>
										<p className='text-xs uppercase text-muted-foreground'>
											Last refresh
										</p>
										<p className='font-medium'>
											{isTrendStatusLoading
												? 'Loading...'
												: formatFreshness(googleTrendsFeed?.lastFetched)}
										</p>
									</div>
									<div>
										<p className='text-xs uppercase text-muted-foreground'>
											Ready topics
										</p>
										<p className='font-medium'>
											{isTrendStatusLoading
												? 'Loading...'
												: googleTrendsStatus?.availableItemsCount ?? 0}
										</p>
									</div>
								</div>

								{(trendError || googleTrendsFeed?.lastError) && (
									<Alert variant='destructive' className='mt-4'>
										<AlertTitle>Buzz Agent Intake Error</AlertTitle>
										<AlertDescription>
											{trendError || googleTrendsFeed?.lastError}
										</AlertDescription>
									</Alert>
								)}

								{googleTrendsStatus?.recentItems?.length ? (
									<div className='mt-5'>
										<p className='mb-3 text-sm font-medium text-muted-foreground'>
											Latest ready topics
										</p>
										<div className='grid gap-2 md:grid-cols-2'>
											{googleTrendsStatus.recentItems.slice(0, 4).map((item) => (
												<a
													key={String(item._id)}
													href={item.url}
													target='_blank'
													rel='noopener noreferrer'
													className='rounded-md border border-purple-500/30 bg-black/20 p-3 text-sm font-medium text-purple-100 hover:bg-purple-900/30'
												>
													{item.title}
												</a>
											))}
										</div>
									</div>
								) : (
									<p className='mt-4 text-sm text-muted-foreground'>
										Refresh Buzz Agents to load current topics for Content Machine generation.
									</p>
								)}
							</div>

							{/* Search Input */}
							<div className='flex items-center gap-2'>
								<Input
									placeholder='Search Buzz Agent topics...'
									value={buzzSearchTerm}
									onChange={(e) => setBuzzSearchTerm(e.target.value)}
									className='max-w-md'
								/>
								{buzzSearchTerm && (
									<Button
										variant='ghost'
										size='sm'
										onClick={() => setBuzzSearchTerm('')}
										className='text-gray-500 hover:text-gray-700'
									>
										Clear
									</Button>
								)}
							</div>
							
							{/* Content container that adjusts to content */}
							<div className='overflow-hidden'>
								<AnimatePresence mode='wait'>
									{isLoadingBuzz ? (
										<motion.div
											key='loader'
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											className='h-full flex justify-center items-center'
										>
											<div className='flex flex-col items-center gap-4'>
												<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500'></div>
												<p className='text-sm text-muted-foreground animate-pulse'>
													Loading buzz agents data...
												</p>
											</div>
										</motion.div>
									) : (
										<motion.div
											key='content'
											initial={{
												opacity: 0,
												y: 50,
											}}
											animate={{ opacity: 1, y: 0 }}
											exit={{
												opacity: 0,
												y: -50,
											}}
											transition={{
												type: 'spring',
												stiffness: 260,
												damping: 20,
											}}
											className='h-full'
										>
											<div className='grid gap-4 h-full'>
												{filteredBuzzItems?.map(
													(
														item: any,
														index: number
													) => (
														<motion.div
															key={item._id}
															initial={{
																opacity: 0,
																y: 50,
															}}
															animate={{
																opacity: 1,
																y: 0,
															}}
															exit={{
																opacity: 0,
																y: -50,
															}}
															transition={{
																duration: 0.2,
																delay:
																	index * 0.1,
															}}
														>
															<Card className='p-4 hover:shadow-lg transition-shadow duration-200 bg-gradient-to-r from-purple-50 to-white'>
																<div className='space-y-3'>
																	<div className='flex items-start justify-between'>
																		<div className='flex-1'>
																			<div className='flex items-center gap-2 mb-1'>
																				<div className='uppercase inline-flex items-center m-4 px-4 py-2 border border-white/70 bg-gradient-to-r from-pink-800  to-pink-500 text-white/90 rounded-full text-sm font-bold text-white shadow-lg'>
																					{
																						item.agentName
																					}
																				</div>
																				{item.isPublic && (
																					<Badge
																						variant='secondary'
																						className='text-xs border border-white/70 bg-gradient-to-r from-purple-900  to-purple-500 text-white/90'
																					>
																						Public
																					</Badge>
																				)}
																			</div>
																			<h3 className='text-lg font-semibold text-purple-600'>
																				{
																					item.headline
																				}
																			</h3>
																		</div>
																		<Badge
																			variant='outline'
																			className='text-xs m-4'
																		>
																			{formatDistanceToNow(
																				new Date(
																					item.createdAt
																				)
																			)}{' '}
																			ago
																		</Badge>
																	</div>

																	<div className='grid grid-cols-2 gap-4 text-sm'>
																		<div>
																			<span className='text-muted-foreground'>
																				Keyword:
																			</span>{' '}
																			<span className='font-medium'>
																				{
																					item.keyword
																				}
																			</span>
																		</div>
																		<div>
																			<span className='text-muted-foreground'>
																				Source:
																			</span>{' '}
																			<span className='font-medium'>
																				{
																					item.source
																				}
																			</span>
																		</div>
																		<div>
																			<span className='text-muted-foreground'>
																				Sentiment:
																			</span>{' '}
																			<span
																				className={`font-medium ${
																					typeof item.sentimentScore === 'number'
																						? item.sentimentScore > 0.5
																							? 'text-green-600'
																							: item.sentimentScore < -0.5
																							? 'text-red-600'
																							: 'text-gray-600'
																						: 'text-gray-600' // Default color for string values
																				}`}
																			>
																				{typeof item.sentimentScore === 'number'
																					? item.sentimentScore.toFixed(2)
																					: item.sentimentScore}
																			</span>
																		</div>
																		<div>
																			<span className='text-muted-foreground'>
																				Score:
																			</span>{' '}
																			<span className='font-medium'>
																				{item.score.toFixed(
																					2
																				)}
																			</span>
																		</div>
																	</div>

																	<div className='space-y-2'>
																		<p className='text-sm text-muted-foreground'>
																			<strong>
																				Summary:
																			</strong>{' '}
																			{
																				item.summary
																			}
																		</p>
																		<p className='text-sm text-muted-foreground'>
																			<strong>
																				Rationale:
																			</strong>{' '}
																			{
																				item.rationale
																			}
																		</p>
																	</div>

																	<div className='flex items-center justify-between pt-2'>
																		<a
																			href={
																				item.url
																			}
																			target='_blank'
																			rel='noopener noreferrer'
																			className='text-sm text-purple-600 hover:text-purple-800 hover:underline'
																		>
																			{truncateUrl(
																				item.url
																			)}
																		</a>
																		<p className='text-xs text-muted-foreground'>
																			{item.author &&
																				`By ${item.author}`}
																		</p>
																	</div>
																</div>
															</Card>
														</motion.div>
													)
												)}
												{buzzResult !== undefined &&
													!filteredBuzzItems?.length && (
														<motion.div
															initial={{
																opacity: 0,
																y: 20,
															}}
															animate={{
																opacity: 1,
																y: 0,
															}}
															className='text-center py-8 text-muted-foreground'
														>
															{buzzSearchTerm
																? `No buzz agents found matching "${buzzSearchTerm}"`
																: 'No buzz agent data available yet'}
														</motion.div>
													)}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>

							{/* Pagination Controls */}
							{(buzzCursor || hasMoreBuzz) && (
								<div className='flex flex-col items-center justify-center gap-4 pt-4 border-t border-gray-200'>
									<div className='flex items-center gap-4'>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setBuzzAnimationDirection(
													'left'
												);
												setBuzzCursor(undefined);
											}}
											disabled={
												!buzzCursor || isLoadingBuzz
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											⏮️ First
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={handlePreviousBuzz}
											disabled={
												!buzzCursor || isLoadingBuzz
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											← Previous
										</Button>
										<div className='flex items-center gap-2'>
											<Button
												variant='outline'
												size='sm'
												className='bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white w-10 h-10'
												disabled
											>
												{buzzCursor ? '2' : '1'}
											</Button>
										</div>
										<Button
											variant='outline'
											size='sm'
											onClick={handleNextBuzz}
											disabled={
												isLoadingBuzz || !hasMoreBuzz
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											Next →
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setBuzzAnimationDirection(
													'right'
												);
												handleNextBuzz();
											}}
											disabled={
												isLoadingBuzz || !hasMoreBuzz
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											Last ⏭️
										</Button>
									</div>
									<div className='text-sm text-gray-400 text-center'>
										<p>
											Page {buzzCursor ? '2' : '1'}
											{hasMoreBuzz
												? ' (more available)'
												: ' (last page)'}
										</p>
										<p className='text-xs mt-1'>
											Showing {filteredBuzzItems.length} buzz
											agent items{buzzSearchTerm ? ' (filtered)' : ' per page'}
										</p>
									</div>
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Research Agent Section */}
				<Card>
					<CardHeader>
						<CardTitle className='text-4xl font-bold mb-2 text-purple-500 flex items-center gap-2'>
							<span className='text-pink-500'>
								Research Agent
							</span>
							<Badge variant='secondary' className='text-sm'>
								Latest Insights
							</Badge>
						</CardTitle>
						<p className='text-muted-foreground'>
							AI Research Agent Results
						</p>
					</CardHeader>
					<CardContent>
						<div className='space-y-6'>
							{/* Search Input */}
							<div className='flex items-center gap-2'>
								<Input
									placeholder='Search research insights...'
									value={researchSearchTerm}
									onChange={(e) => setResearchSearchTerm(e.target.value)}
									className='max-w-md'
								/>
								{researchSearchTerm && (
									<Button
										variant='ghost'
										size='sm'
										onClick={() => setResearchSearchTerm('')}
										className='text-gray-500 hover:text-gray-700'
									>
										Clear
									</Button>
								)}
							</div>
							
							{/* Content container that adjusts to content */}
							<div className='overflow-hidden'>
								<AnimatePresence mode='wait'>
									{isLoadingResearch ? (
										<motion.div
											key='loader'
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											className='h-full flex justify-center items-center'
										>
											<div className='flex flex-col items-center gap-4'>
												<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500'></div>
												<p className='text-sm text-muted-foreground animate-pulse'>
													Loading insights...
												</p>
											</div>
										</motion.div>
									) : (
										<motion.div
											key='content'
											initial={{
												opacity: 0,
												y: 50,
											}}
											animate={{ opacity: 1, y: 0 }}
											exit={{
												opacity: 0,
												y: -50,
											}}
											transition={{
												type: 'spring',
												stiffness: 260,
												damping: 20,
											}}
											className='h-full'
										>
											<div className='grid gap-4 h-full'>
												{filteredResearchItems?.map(
													(
														item: any,
														index: number
													) => (
														<motion.div
															key={item._id}
															initial={{
																opacity: 0,
																y: 50,
															}}
															animate={{
																opacity: 1,
																y: 0,
															}}
															exit={{
																opacity: 0,
																y: -50,
															}}
															transition={{
																duration: 0.2,
																delay:
																	index * 0.1,
															}}
														>
															<Card className='p-4 hover:shadow-lg transition-shadow duration-200 bg-gradient-to-r from-purple-50 to-white'>
																<div className='space-y-2'>
																	<div className='flex items-center justify-between'>
																		<h3 className='text-lg font-semibold text-purple-600'>
																			{
																				item.subject
																			}
																		</h3>
																		<Badge
																			variant='outline'
																			className='text-xs'
																		>
																			{formatDistanceToNow(
																				new Date(
																					item.createdAt
																				)
																			)}{' '}
																			ago
																		</Badge>
																	</div>
																	<p className='text-sm text-muted-foreground line-clamp-3'>
																		{
																			item.rawResponse
																		}
																	</p>
																</div>
															</Card>
														</motion.div>
													)
												)}
												{!filteredResearchItems?.length && (
													<motion.div
														initial={{
															opacity: 0,
															y: 20,
														}}
														animate={{
															opacity: 1,
															y: 0,
														}}
														className='text-center py-8 text-muted-foreground'
													>
														{researchSearchTerm
															? `No research insights found matching "${researchSearchTerm}"`
															: 'No research insights available yet'}
													</motion.div>
												)}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>

							{/* Pagination Controls */}
							{(researchCursor || hasMoreResearch) && (
								<div className='flex flex-col items-center justify-center gap-4 pt-4 border-t border-gray-200'>
									<div className='flex items-center gap-4'>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setAnimationDirection('left');
												setResearchCursor(undefined);
											}}
											disabled={
												!researchCursor ||
												isLoadingResearch
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											⏮️ First
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={handlePreviousResearch}
											disabled={
												!researchCursor ||
												isLoadingResearch
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											← Previous
										</Button>
										<div className='flex items-center gap-2'>
											<Button
												variant='outline'
												size='sm'
												className='bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white w-10 h-10'
												disabled
											>
												{researchCursor ? '2' : '1'}
											</Button>
										</div>
										<Button
											variant='outline'
											size='sm'
											onClick={handleNextResearch}
											disabled={
												isLoadingResearch ||
												!hasMoreResearch
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											Next →
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setAnimationDirection('right');
												handleNextResearch();
											}}
											disabled={
												isLoadingResearch ||
												!hasMoreResearch
											}
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											Last ⏭️
										</Button>
									</div>
									<div className='text-sm text-gray-400 text-center'>
										<p>
											Page {researchCursor ? '2' : '1'}
											{hasMoreResearch
												? ' (more available)'
												: ' (last page)'}
										</p>
										<p className='text-xs mt-1'>
											Showing {filteredResearchItems.length}{' '}
											research insights{researchSearchTerm ? ' (filtered)' : ' per page'}
										</p>
									</div>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			</div>

			<Confetti />
		</div>
	);
}
